// ═══════════════════════════════════════════════════════════════════════
// lldTab.jsx — Low-Level Design deep dive per interface
// ═══════════════════════════════════════════════════════════════════════
// For any interface, render the full LLD: integration structure, sequence,
// APIs, error handling, security, monitoring, rollback, SLA, dependencies,
// implementation order, technical details. Sources: CLM_LLD.pptx +
// CLM_LLD_PerInterface.pptx + CLM_LLD_Companion.pptx, plus the live data
// already on IFACES (apis, integrationStructure, implementationOrder, etc.)
// ═══════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import {
  IFACES, GRP_META, MVP_META, PHASE_META, RISK_META, CX_META,
  getInterfaceProgress, getInterfaceDependencies, getDependentInterfaces,
} from "./IfacesData";
import {
  getProtocolsByInterface, getSecurityProfile,
} from "./IF_Protocols";
import { T, FONTS, pill as pillStyle, selectStyle } from "./theme";
import {
  getAgentsForInterface, getBenefitsForInterface,
  getFlowsForInterface, getGatesForInterface,
  getB2MMLSchemasForInterface,
} from "./crossRefs";
import { EntityRefList } from "./navigation";
import {
  FLOWS as TAXONOMY_FLOWS, L1_CATEGORIES, SEVERITY_META,
  INTERFACE_TYPES, PAYLOAD_TYPES,
} from "./interfaceTaxonomy";
import { schemaById, contractsBySchema } from "./b2mmlData";

export default function LLDTab({ state }) {
  const [search, setSearch]         = useState("");
  const [filterGrp, setFilterGrp]   = useState("ALL");
  const [filterMVP, setFilterMVP]   = useState("ALL");
  const [selectedId, setSelectedId] = useState(state?.selectedInterface || null);

  // When another tab navigates here via nav.goToInterface(id), App.jsx
  // sets state.selectedInterface and switches to LLD. Pick it up here so
  // the deep-dive shows the requested interface instead of staying blank.
  useEffect(() => {
    if (state?.selectedInterface && state.selectedInterface !== selectedId) {
      setSelectedId(state.selectedInterface);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.selectedInterface]);

  const interfaces = IFACES.filter(f => {
    if (filterGrp !== "ALL" && f.grp !== filterGrp) return false;
    if (filterMVP !== "ALL" && String(f.mvp) !== filterMVP) return false;
    if (search) {
      const hay = `${f.id} ${f.name} ${f.bo} ${f.from} ${f.to}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const selected = selectedId ? IFACES.find(f => f.id === selectedId) : null;
  const groups   = [...new Set(IFACES.map(f => f.grp))].sort();

  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          📐 Low-Level Design (LLD) Explorer
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          Per-interface deep-dive: architecture · APIs · errors · security · monitoring · rollback · dependencies
        </div>
      </div>

      {/* Filter bar */}
      <div style={{
        display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap",
        padding: "12px 14px", background: T.bg2, borderRadius: 8, border: `1px solid ${T.border}`,
      }}>
        <input type="text" placeholder="🔍 Search interface..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{
            background: T.bg3, border: `1px solid ${T.border}`, color: T.text,
            padding: "5px 10px", borderRadius: 4, fontSize: 10, outline: "none", minWidth: 240,
          }} />
        <select value={filterGrp} onChange={e => setFilterGrp(e.target.value)} style={selectStyle}>
          <option value="ALL">All groups</option>
          {groups.map(g => <option key={g} value={g}>{GRP_META[g]?.label || g}</option>)}
        </select>
        <select value={filterMVP} onChange={e => setFilterMVP(e.target.value)} style={selectStyle}>
          <option value="ALL">All MVPs</option>
          <option value="1">MVP-1</option>
          <option value="2">MVP-2</option>
          <option value="3">MVP-3</option>
        </select>
        <div style={{ marginLeft: "auto", fontSize: 10, color: T.text2 }}>
          {interfaces.length} interfaces shown
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 14 }}>
        {/* Interface list */}
        <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
          <div style={{
            padding: "10px 14px", borderBottom: `1px solid ${T.border}`,
            fontSize: 10, color: T.cyan, fontWeight: 700, letterSpacing: 1,
          }}>
            INTERFACES ({interfaces.length})
          </div>
          <div style={{ maxHeight: 700, overflowY: "auto" }}>
            {interfaces.map(f => {
              const grp = GRP_META[f.grp] || { color: T.steel };
              const isSel = selectedId === f.id;
              const prog = getInterfaceProgress(f, state?.dodState, state?.nisState);
              return (
                <div key={f.id} onClick={() => setSelectedId(f.id)}
                  style={{
                    padding: "8px 12px", cursor: "pointer",
                    borderBottom: `1px solid ${T.border2}`,
                    background: isSel ? `${grp.color}15` : "transparent",
                    borderLeft: `3px solid ${isSel ? grp.color : "transparent"}`,
                  }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontFamily: FONTS.header, fontSize: 9, color: grp.color, fontWeight: 700 }}>
                      {f.id}
                    </span>
                    <span style={{ fontSize: 8, color: prog.pct === 100 ? T.green : T.amber, fontWeight: 700 }}>
                      {prog.pct}%
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: isSel ? T.text : T.text2, fontWeight: isSel ? 600 : 400 }}>
                    {f.name.length > 36 ? f.name.substring(0, 36) + "…" : f.name}
                  </div>
                  <div style={{ fontSize: 8, color: T.text3, marginTop: 2 }}>
                    {f.grp} · MVP-{f.mvp} · P{f.phase}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* LLD detail */}
        <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
          {!selected ? (
            <div style={{ padding: 40, textAlign: "center", color: T.text3, fontStyle: "italic" }}>
              ← Select an interface to view its complete LLD
            </div>
          ) : (
            <LLDDetail iface={selected} state={state} />
          )}
        </div>
      </div>
    </div>
  );
}

function LLDDetail({ iface, state }) {
  const grp = GRP_META[iface.grp] || { color: T.steel };
  const proto = getProtocolsByInterface(iface.id);
  const sec = getSecurityProfile();
  const deps = getInterfaceDependencies(iface.id);
  const dependents = getDependentInterfaces(iface.id);
  const prog = getInterfaceProgress(iface, state?.dodState, state?.nisState);

  return (
    <div>
      {/* Header */}
      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, background: T.bg3 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
          <div style={{
            fontFamily: FONTS.header, fontSize: 22, fontWeight: 700,
            color: grp.color, padding: "4px 10px",
            background: `${grp.color}10`, borderRadius: 4,
          }}>
            {iface.id}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>{iface.name}</div>
            <div style={{ fontSize: 10, color: T.text2, fontFamily: "monospace" }}>
              {iface.from} <span style={{ color: T.cyan }}>→</span> {iface.to}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span style={pillStyle(grp.color)}>{iface.grp}</span>
          <span style={pillStyle(MVP_META[iface.mvp]?.c || T.cyan)}>MVP-{iface.mvp}</span>
          <span style={pillStyle(PHASE_META[iface.phase]?.c || T.cyan)}>Phase {iface.phase}</span>
          <span style={pillStyle(CX_META[iface.complexity]?.c || T.cyan)}>{CX_META[iface.complexity]?.l}</span>
          <span style={pillStyle(RISK_META[iface.risk]?.c || T.steel)}>{RISK_META[iface.risk]?.l} risk</span>
          <span style={pillStyle(T.purple)}>{iface.effort}d effort</span>
          {iface.freq && <span style={pillStyle(T.amber)}>{iface.freq}</span>}
        </div>
      </div>

      <div style={{ padding: "16px 18px", maxHeight: 700, overflowY: "auto" }}>
        {/* NEW · Intelligence layer — surfaces the rich `enhanced` field */}
        {iface.enhanced && (
          <Section title="💡 INTELLIGENCE LAYER" color={T.purple}>
            <IntelligenceLayer enhanced={iface.enhanced}/>
          </Section>
        )}

        {/* Cross-tab navigation — what touches this interface */}
        <Section title="🔗 CROSS-REFERENCES" color={T.cyan}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            <EntityRefList kind="agent"   ids={getAgentsForInterface(iface.id)}
              label="🤖 Agents touching this" emptyText="(no agentic coverage yet)" />
            <EntityRefList kind="benefit" ids={getBenefitsForInterface(iface.id)}
              label="🎯 Benefits delivered" emptyText="(not directly tied to a tracked benefit)" />
            <EntityRefList kind="flow"    ids={getFlowsForInterface(iface.id)}
              label="🚌 ESB flows" emptyText="(not on a tier-1 ESB flow)" />
            <EntityRefList kind="gate"    ids={getGatesForInterface(iface.id)}
              label="🛡️ Human gates protecting this" emptyText="(no explicit gate)" />
          </div>
        </Section>

        {/* NEW · Taxonomy classification — what kind of interface is this */}
        <Section title="🗂 TAXONOMY CLASSIFICATION" color={T.amber}>
          <TaxonomyMatches iface={iface} />
        </Section>

        {/* NEW · B2MML federation context — which canonical schemas + contracts */}
        <Section title="🏛 B2MML FEDERATION CONTEXT" color={T.green}>
          <B2MMLContext iface={iface} />
        </Section>

        {/* About */}
        {iface.about && (
          <Section title="📌 ABOUT" color={grp.color}>
            <div style={{ fontSize: 10, color: T.text2, lineHeight: 1.6 }}>{iface.about}</div>
          </Section>
        )}

        {/* Integration architecture */}
        {iface.integrationStructure && (
          <Section title="🔧 INTEGRATION ARCHITECTURE" color={T.cyan}>
            <KVGrid items={[
              { k: "Architecture", v: iface.integrationStructure.architecture },
              { k: "Data Flow",    v: iface.integrationStructure.dataFlow },
              { k: "Protocol",     v: iface.protocol },
              { k: "Frequency",    v: iface.freq },
            ]} />
            {iface.integrationStructure.components?.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 8, color: T.text2, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Components</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {iface.integrationStructure.components.map((c, i) => (
                    <div key={i} style={{
                      background: `${T.blue}15`, padding: "4px 10px", borderRadius: 4,
                      fontSize: 9, color: T.blue, border: `1px solid ${T.blue}30`,
                    }}>
                      <strong>{c.name}:</strong> {c.role}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Protocol detail */}
        {proto && (
          <Section title="🔌 PROTOCOL & SLA" color={T.purple}>
            <KVGrid items={[
              { k: "Primary",   v: proto.primaryProtocol },
              { k: "Secondary", v: Array.isArray(proto.secondaryProtocol)
                                    ? proto.secondaryProtocol.join(" · ")
                                    : proto.secondaryProtocol || "—" },
              { k: "Data format",   v: proto.dataFormat },
              { k: "Sync strategy", v: proto.syncStrategy },
              { k: "SLA typical",   v: proto.latencySLA?.typical },
              { k: "SLA max",       v: proto.latencySLA?.max },
              { k: "Security",      v: proto.security },
            ]} />
          </Section>
        )}

        {/* Implementation order */}
        {iface.implementationOrder?.length > 0 && (
          <Section title="📋 IMPLEMENTATION ORDER" color={T.gold}>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 10, color: T.text2, lineHeight: 1.8 }}>
              {iface.implementationOrder.map((item, i) => {
                if (typeof item === "string") return <li key={i}>{item}</li>;
                return (
                  <li key={i}>
                    <strong style={{ color: T.cyan }}>Step {item.step}:</strong> {item.action}
                    {item.duration && <span style={{ color: T.text3, marginLeft: 8 }}>({item.duration})</span>}
                    {item.owner && <span style={{ color: T.amber, marginLeft: 8 }}>· {item.owner}</span>}
                  </li>
                );
              })}
            </ol>
          </Section>
        )}

        {/* APIs */}
        {iface.apis?.length > 0 && (
          <Section title="🔌 API SPECIFICATIONS" color={T.blue}>
            {iface.apis.map((api, i) => (
              <div key={i} style={{
                background: T.bg3, border: `1px solid ${T.blue}25`, borderRadius: 6,
                padding: "10px 14px", marginBottom: 8,
              }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                  <span style={{
                    fontFamily: "monospace", fontSize: 9, padding: "2px 8px",
                    background: `${T.blue}25`, borderRadius: 4, color: T.blue, fontWeight: 700,
                  }}>{api.method}</span>
                  <span style={{ fontWeight: 600, fontSize: 11, color: T.text }}>{api.name}</span>
                </div>
                <div style={{ fontSize: 9.5, color: T.text2, marginBottom: 4 }}>{api.purpose}</div>
                <div style={{ fontSize: 9 }}>
                  <span style={{ color: T.text2 }}>URL: </span>
                  <span style={{ color: T.cyan, fontFamily: "monospace" }}>{api.url}</span>
                </div>
                {api.sample && (
                  <pre style={{
                    background: T.bg2, padding: 10, marginTop: 6, borderRadius: 4,
                    fontSize: 8.5, color: T.text2, overflowX: "auto",
                    border: `1px solid ${T.border}`, fontFamily: "monospace",
                  }}>{api.sample}</pre>
                )}
              </div>
            ))}
          </Section>
        )}

        {/* Triggers — direct */}
        {iface.triggers?.length > 0 && (
          <Section title="⚡ TRIGGERS" color={T.pink}>
            {iface.triggers.map(t => (
              <div key={t.id} style={{
                background: T.bg3, border: `1px solid ${T.pink}25`, borderLeft: `3px solid ${T.pink}`,
                borderRadius: 6, padding: "10px 14px", marginBottom: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, color: T.pink, fontSize: 11 }}>{t.name}</span>
                  <span style={pillStyle(T.amber)}>{t.type}</span>
                </div>
                <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5, marginBottom: 4 }}>{t.description}</div>
                {t.latency && <div style={{ fontSize: 9, color: T.cyan }}>⏱ {t.latency}</div>}
              </div>
            ))}
          </Section>
        )}

        {/* NEW · Attribute mapping (canonical) */}
        {iface.attrMap && iface.attrMap.length > 0 && (
          <Section title="🔀 ATTRIBUTE MAPPING" color={T.gold}>
            <AttributeMappingTable mappings={iface.attrMap}/>
          </Section>
        )}

        {/* NEW · Tasks with hierarchical sub-tasks */}
        {iface.tasks && iface.tasks.length > 0 && (
          <Section title="🛠️ TASKS · SUB-TASKS · DOD" color={T.orange}>
            <TasksWithSubTasks tasks={iface.tasks} ifaceId={iface.id} state={state}/>
          </Section>
        )}

        {/* Dependencies */}
        {(deps.length > 0 || dependents.length > 0) && (
          <Section title="🔗 DEPENDENCIES" color={T.teal}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <div style={{ fontSize: 9, color: T.amber, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
                  ⬅ This interface DEPENDS ON ({deps.length})
                </div>
                {deps.length === 0 ? (
                  <div style={{ fontSize: 9, color: T.text3, fontStyle: "italic" }}>No upstream dependencies.</div>
                ) : deps.map(d => (
                  <div key={d.id} style={{ fontSize: 9, color: T.text2, marginBottom: 3 }}>
                    <span style={{ color: T.amber, fontFamily: "monospace" }}>{d.id}</span> · {d.name}
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 9, color: T.cyan, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
                  Used by ({dependents.length}) ➡
                </div>
                {dependents.length === 0 ? (
                  <div style={{ fontSize: 9, color: T.text3, fontStyle: "italic" }}>No downstream consumers.</div>
                ) : dependents.map(d => (
                  <div key={d.id} style={{ fontSize: 9, color: T.text2, marginBottom: 3 }}>
                    <span style={{ color: T.cyan, fontFamily: "monospace" }}>{d.id}</span> · {d.name}
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* DoD progress */}
        <Section title="✅ DEFINITION OF DONE" color={T.green}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <div style={{ flex: 1, height: 6, background: T.bg3, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${prog.pct}%`,
                            background: prog.pct === 100 ? T.green : T.amber, borderRadius: 3 }} />
            </div>
            <span style={{ fontFamily: FONTS.header, fontSize: 12,
                           color: prog.pct === 100 ? T.green : T.amber, fontWeight: 700 }}>
              {prog.pct}% · {prog.done}/{prog.total}
            </span>
          </div>
          <div style={{ fontSize: 9, color: T.text2 }}>
            {iface.tasks?.length || 0} tasks · {prog.total} total DoD items
          </div>
        </Section>

        {/* Security profile (global, but shown per-interface for context) */}
        <Section title="🔒 SECURITY PROFILE (program-wide)" color={T.red}>
          <KVGrid items={[
            { k: "Encrypt at rest",    v: sec.encryptionAtRest },
            { k: "Encrypt transit",    v: sec.encryptionInTransit },
            { k: "IT auth",            v: sec.authentication?.itLayer },
            { k: "OT auth",            v: sec.authentication?.otLayer },
            { k: "API auth",           v: sec.authentication?.apiLayer },
            { k: "Authorization",      v: sec.authorization },
            { k: "Audit logging",      v: sec.auditLogging },
            { k: "Secret management",  v: sec.secretManagement },
          ]} />
        </Section>
      </div>
    </div>
  );
}

function Section({ title, color, children }) {
  return (
    <div style={{
      marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${T.border2}`,
    }}>
      <div style={{
        fontSize: 9, fontFamily: FONTS.mono,
        color: color, fontWeight: 700, letterSpacing: 1.5, marginBottom: 8,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function KVGrid({ items }) {
  const filtered = items.filter(i => i.v != null && i.v !== "");
  if (filtered.length === 0) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
      {filtered.map(i => (
        <div key={i.k} style={{ background: T.bg3, padding: "6px 10px", borderRadius: 4 }}>
          <div style={{ fontSize: 7.5, color: T.text3, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>
            {i.k}
          </div>
          <div style={{ fontSize: 10, color: T.text, fontFamily: "monospace", wordBreak: "break-word" }}>
            {String(i.v)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── TaxonomyMatches · classify a CLM interface against the 107-flow taxonomy ──
function TaxonomyMatches({ iface }) {
  // Heuristic match: search across the interface's name/bo/from/to/protocol
  // against each taxonomy flow's L3 name + examples + protocol.
  const hay = `${iface.name || ""} ${iface.bo || ""} ${iface.from || ""} ${iface.to || ""} ${iface.protocol || ""}`.toLowerCase();

  const matches = TAXONOMY_FLOWS.map(f => {
    const tokens = [
      f.l3.toLowerCase(),
      ...f.examples.toLowerCase().split(",").map(s => s.trim()),
      f.protocol.toLowerCase(),
    ].filter(t => t.length > 4);
    let score = 0;
    for (const t of tokens) {
      if (hay.includes(t)) score++;
    }
    return { flow: f, score };
  }).filter(m => m.score > 0).sort((a, b) => b.score - a.score).slice(0, 4);

  if (matches.length === 0) {
    return (
      <div style={{ fontSize: 9.5, color: T.text3, fontStyle: "italic" }}>
        No strong taxonomy match found heuristically. Open the Taxonomy tab to classify manually.
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 8 }}>
      {matches.map(({ flow, score }) => {
        const cat = L1_CATEGORIES.find(c => c.id === flow.l1);
        const sev = SEVERITY_META[flow.severity];
        const ifT = INTERFACE_TYPES[flow.interfaceType];
        const pT  = PAYLOAD_TYPES[flow.payloadType];
        return (
          <div key={flow.id} style={{
            background: T.bg3,
            border: `1px solid ${cat?.color}40`,
            borderLeft: `3px solid ${cat?.color}`,
            borderRadius: 4, padding: "8px 10px",
          }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 8, color: T.text3 }}>
                #{String(flow.id).padStart(3,"0")}
              </span>
              <span style={pillStyle(cat?.color)}>{flow.l1}</span>
              <span style={{
                fontSize: 8, fontFamily: FONTS.mono, color: sev.color, fontWeight: 700,
                padding: "1px 5px", borderRadius: 3,
                background: `${sev.color}20`,
              }}>S{flow.severity}</span>
              <span style={{ marginLeft: "auto", fontSize: 8, color: T.text3 }}>
                {score} match{score>1?"es":""}
              </span>
            </div>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.text, marginBottom: 4 }}>
              {flow.l3}
            </div>
            <div style={{ fontSize: 8.5, color: T.text2, marginBottom: 4 }}>
              {ifT?.icon} {ifT?.label} · {pT?.label}
            </div>
            <div style={{ fontSize: 8, color: cat?.color, fontFamily: FONTS.mono }}>
              {flow.protocol}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── B2MMLContext · which B2MML schemas + contracts carry this interface ───
function B2MMLContext({ iface }) {
  const schemas = getB2MMLSchemasForInterface(iface.id);

  if (schemas.length === 0) {
    return (
      <div style={{ fontSize: 9.5, color: T.text3, fontStyle: "italic" }}>
        This interface is not part of any B2MML/ISA-95 federation contract.
        It runs at a tier (e.g., L2↔L1 control loops, telemetry, internal app calls) where canonical XML envelopes are not the right abstraction.
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
      {schemas.map(schemaId => {
        const schema = schemaById(schemaId);
        if (!schema) return null;
        const contracts = contractsBySchema(schemaId);
        return (
          <div key={schemaId} style={{
            background: T.bg3,
            border: `1px solid ${schema.color}40`,
            borderLeft: `3px solid ${schema.color}`,
            borderRadius: 4, padding: "10px 12px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>{schema.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: schema.color }}>
                  {schema.name}
                </div>
                <div style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono }}>
                  {schema.isaPart}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 9, color: T.text2, marginBottom: 6, lineHeight: 1.4 }}>
              {schema.purpose}
            </div>
            <div style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono, marginBottom: 4 }}>
              MASTER DATA: <span style={{ color: T.amber }}>{schema.masterDataAt}</span>
            </div>
            <div style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono }}>
              CONTRACTS ({contracts.length}):
            </div>
            {contracts.slice(0, 3).map(c => (
              <div key={c.id} style={{ fontSize: 8.5, color: T.text2, marginTop: 3, paddingLeft: 8 }}>
                <span style={{ color: c.semantic === "exactly-once" ? T.red : T.green, fontFamily: FONTS.mono }}>
                  {c.fromLevel}{c.direction === "bidirectional" ? "↔" : "→"}{c.toLevel}
                </span>
                {" · "}{c.sla}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ─── IntelligenceLayer · surfaces the enhanced{} field ──────────────────
function IntelligenceLayer({ enhanced }) {
  const e = enhanced || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 10 }}>
      {/* Meaning + Purpose (full-width) */}
      {(e.meaning || e.purpose) && (
        <div style={{ gridColumn: "1 / -1", background: T.bg3, border: `1px solid ${T.purple}30`,
          borderLeft: `3px solid ${T.purple}`, borderRadius: 4, padding: "10px 12px" }}>
          {e.meaning && (
            <>
              <div style={{ fontSize: 8, color: T.purple, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>
                🌐 MEANING
              </div>
              <div style={{ fontSize: 10, color: T.text, lineHeight: 1.5, marginBottom: 8 }}>{e.meaning}</div>
            </>
          )}
          {e.purpose && (
            <>
              <div style={{ fontSize: 8, color: T.green, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>
                🎯 PURPOSE
              </div>
              <div style={{ fontSize: 10, color: T.text, lineHeight: 1.5 }}>{e.purpose}</div>
            </>
          )}
        </div>
      )}

      {/* Criticality + Reusability + Hours */}
      <KVCard color={T.amber} title="💎 SIGNATURE" rows={[
        e.criticalityScore !== undefined && { k: "Criticality score", v: e.criticalityScore + " / 25" },
        e.taskHours !== undefined && { k: "Task hours", v: e.taskHours + "h" },
        e.moscow && { k: "MoSCoW", v: e.moscow },
        e.reusability && { k: "Reusability", v: e.reusability },
      ].filter(Boolean)}/>

      <KVCard color={T.cyan} title="🏛️ ARCHITECTURE" rows={[
        e.architecture && { k: "Architecture", v: e.architecture },
        e.interfacePattern && { k: "Pattern", v: e.interfacePattern },
        e.harmoniousFlowChannel && { k: "Channel", v: e.harmoniousFlowChannel },
      ].filter(Boolean)}/>

      <KVCard color={T.green} title="📦 SCOPE" rows={[
        e.sourceBusinessObjectsInScope && { k: "Source BOs", v: e.sourceBusinessObjectsInScope },
        e.destinationBusinessObjectsInScope && { k: "Destination BOs", v: e.destinationBusinessObjectsInScope },
        e.systemOfRecord && { k: "System of record", v: e.systemOfRecord },
      ].filter(Boolean)}/>

      <KVCard color={T.red} title="🛡️ RESILIENCE" rows={[
        e.idempotencyStrategy && { k: "Idempotency", v: e.idempotencyStrategy },
        e.errorHandlingStrategy && { k: "Error handling", v: e.errorHandlingStrategy },
        e.securityCompliance && { k: "Security", v: e.securityCompliance },
        e.monitoringSignals && { k: "Monitoring", v: e.monitoringSignals },
      ].filter(Boolean)}/>

      {/* Sub-functions (full-width if present) */}
      {e.subFunctions && e.subFunctions.length > 0 && (
        <div style={{ gridColumn: "1 / -1", background: T.bg3, border: `1px solid ${T.cyan}30`,
          borderLeft: `3px solid ${T.cyan}`, borderRadius: 4, padding: "10px 12px" }}>
          <div style={{ fontSize: 8, color: T.cyan, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>
            🔧 SUB-FUNCTIONS ({e.subFunctions.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {e.subFunctions.map((fn, i) => (
              <span key={i} style={{
                fontSize: 9, padding: "3px 9px",
                background: `${T.cyan}15`, color: T.cyan,
                border: `1px solid ${T.cyan}40`, borderRadius: 12,
                fontFamily: FONTS.mono,
              }}>{i + 1}. {fn}</span>
            ))}
          </div>
        </div>
      )}

      {/* Doc search keywords */}
      {e.docSearchKeywords && e.docSearchKeywords.length > 0 && (
        <div style={{ gridColumn: "1 / -1", background: T.bg3, border: `1px solid ${T.gold}30`,
          borderRadius: 4, padding: "10px 12px" }}>
          <div style={{ fontSize: 8, color: T.gold, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>
            📚 SIEMENS DOCUMENTATION SEARCH KEYWORDS
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {e.docSearchKeywords.map((kw, i) => (
              <span key={i} style={{
                fontSize: 8.5, padding: "2px 7px",
                background: T.bg, color: T.gold,
                border: `1px solid ${T.gold}40`, borderRadius: 3,
                fontFamily: FONTS.mono,
              }}>{kw}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KVCard({ color, title, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div style={{
      background: T.bg3, border: `1px solid ${color}30`,
      borderLeft: `3px solid ${color}`, borderRadius: 4, padding: "10px 12px",
    }}>
      <div style={{ fontSize: 8, color, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>
        {title}
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ marginBottom: 5 }}>
          <div style={{ fontSize: 7, color: T.text3, letterSpacing: 1, textTransform: "uppercase", marginBottom: 1 }}>
            {r.k}
          </div>
          <div style={{ fontSize: 9.5, color: T.text, lineHeight: 1.4 }}>{r.v}</div>
        </div>
      ))}
    </div>
  );
}

// ─── AttributeMappingTable · canonical src → dst mappings ──────────────
function AttributeMappingTable({ mappings }) {
  const byCategory = mappings.reduce((m, x) => {
    const cat = x.category || "default";
    if (!m[cat]) m[cat] = [];
    m[cat].push(x);
    return m;
  }, {});

  const CAT_COLOR = { core: T.cyan, quality: T.pink, additional: T.steel, default: T.gold };

  return (
    <div>
      <div style={{ fontSize: 9, color: T.text2, marginBottom: 10, fontStyle: "italic" }}>
        {mappings.length} field mapping{mappings.length !== 1 ? "s" : ""} ·
        {" "}{mappings.filter(m => m.mandatory).length} mandatory ·
        {" "}{mappings.filter(m => !m.mandatory).length} optional
      </div>

      {Object.entries(byCategory).map(([cat, list]) => {
        const c = CAT_COLOR[cat] || T.gold;
        return (
          <div key={cat} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 8, color: c, letterSpacing: 1, fontWeight: 700, marginBottom: 6, textTransform: "uppercase" }}>
              {cat} ({list.length})
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
              <thead>
                <tr style={{ background: T.bg3 }}>
                  <th style={{ padding: "5px 8px", textAlign: "left", color: T.text3, fontFamily: FONTS.mono, letterSpacing: 1 }}>SOURCE</th>
                  <th style={{ padding: "5px 8px", textAlign: "left", color: T.text3, fontFamily: FONTS.mono, letterSpacing: 1 }}>→</th>
                  <th style={{ padding: "5px 8px", textAlign: "left", color: T.text3, fontFamily: FONTS.mono, letterSpacing: 1 }}>DESTINATION</th>
                  <th style={{ padding: "5px 8px", textAlign: "left", color: T.text3, fontFamily: FONTS.mono, letterSpacing: 1 }}>TRANSFORM</th>
                </tr>
              </thead>
              <tbody>
                {list.map((m, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.border2}` }}>
                    <td style={{ padding: "5px 8px", color: T.cyan, fontFamily: FONTS.mono, fontSize: 9 }}>{m.src}</td>
                    <td style={{ padding: "5px 8px", color: T.text3 }}>→</td>
                    <td style={{ padding: "5px 8px", color: T.green, fontFamily: FONTS.mono, fontSize: 9 }}>
                      {m.dst}
                      {m.mandatory && <span style={{ marginLeft: 6, fontSize: 7, color: T.red, fontWeight: 700 }}>REQUIRED</span>}
                    </td>
                    <td style={{ padding: "5px 8px", color: T.amber, fontSize: 9 }}>{m.xform}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// ─── TasksWithSubTasks · hierarchical tasks display ─────────────────────
function TasksWithSubTasks({ tasks, ifaceId, state }) {
  const [expandedTask, setExpandedTask] = useState(null);

  const IMPL_COLOR = {
    "OOTB": T.green,
    "OOTB + Config": T.cyan,
    "Customization": T.red,
  };

  return (
    <div>
      {tasks.map(t => {
        const c = IMPL_COLOR[t.implementationType] || T.steel;
        const isOpen = expandedTask === t.id;
        const completedDoD = (t.dod || []).filter((_, i) => state?.dodState?.[`${t.id}-${i}`]).length;
        const totalDoD = t.dod?.length || 0;
        const pct = totalDoD > 0 ? (completedDoD / totalDoD) * 100 : 0;

        return (
          <div key={t.id} style={{
            background: T.bg3,
            border: `1px solid ${c}30`,
            borderLeft: `3px solid ${c}`,
            borderRadius: 4, marginBottom: 8,
          }}>
            <div onClick={() => setExpandedTask(isOpen ? null : t.id)}
              style={{ padding: "8px 12px", cursor: "pointer", display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ color: c, fontFamily: FONTS.mono, fontSize: 12, fontWeight: 700 }}>
                {isOpen ? "▾" : "▸"}
              </span>
              <span style={{ fontFamily: FONTS.mono, fontSize: 8, color: T.text3, minWidth: 50 }}>{t.id}</span>
              <span style={{ fontSize: 11, color: T.text, fontWeight: 600, flex: 1 }}>{t.name}</span>
              <span style={pillStyle(c)}>{t.implementationType}</span>
              <span style={{ fontSize: 9, color: T.amber, fontFamily: FONTS.mono, fontWeight: 700, minWidth: 40, textAlign: "right" }}>
                {t.hours}h
              </span>
              {totalDoD > 0 && (
                <span style={{ fontSize: 9, color: pct === 100 ? T.green : T.text2, fontFamily: FONTS.mono, minWidth: 50, textAlign: "right" }}>
                  {completedDoD}/{totalDoD}
                </span>
              )}
            </div>

            {isOpen && (
              <div style={{ padding: "0 12px 12px 32px" }}>
                {t.classificationRationale && (
                  <div style={{
                    background: `${c}10`, border: `1px solid ${c}30`,
                    padding: "6px 10px", borderRadius: 3, marginBottom: 8, fontSize: 9, color: T.text2, fontStyle: "italic",
                  }}>
                    <strong style={{ color: c }}>Why {t.implementationType}: </strong>
                    {t.classificationRationale}
                  </div>
                )}

                {t.subTasks && t.subTasks.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 8, color: T.text3, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>
                      EXECUTION STEPS ({t.subTasks.length})
                    </div>
                    {t.subTasks.map(st => (
                      <div key={st.step} style={{ marginBottom: 6, paddingLeft: 10, borderLeft: `2px solid ${T.border2}` }}>
                        <div style={{ fontSize: 9.5, color: T.text, fontWeight: 600 }}>
                          <span style={{ color: c, marginRight: 6 }}>{st.step}.</span>
                          {st.title}
                        </div>
                        {st.subSubTasks && (
                          <ul style={{ margin: "2px 0 0", paddingLeft: 18, fontSize: 9, color: T.text2 }}>
                            {st.subSubTasks.map((sst, i) => <li key={i}>{sst}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {t.dod && t.dod.length > 0 && (
                  <div>
                    <div style={{ fontSize: 8, color: T.green, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>
                      DEFINITION OF DONE ({completedDoD}/{totalDoD})
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 9, color: T.text2 }}>
                      {t.dod.map((d, i) => {
                        const done = !!state?.dodState?.[`${t.id}-${i}`];
                        return (
                          <li key={i} style={{ color: done ? T.green : T.text2, textDecoration: done ? "line-through" : "none" }}>
                            {done ? "✓" : "○"} {d}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
